import { PlessViewUtilsSub } from './pless-view-utils-sub.service';
import { SectionComponent } from '../../kymw/kymw14/kymw1400/page/section.component';
import { KomaComponent } from '../../kymw/kymw14/kymw1400/page/koma.component';

/**
 * 数値に関する便利機能を提供するUtil
 */
export class CalcUtil {
  /**
   * 数値でないかどうかを判定する(string版)
   * [戻り値]true：数値ではない／false：数値
   *
   * @param value 判定する文字列
   */
  static isNaNString(value: string): boolean {
    // 値が無い場合は判定できないのでfalseを返す
    if (value == null || value === '') {
      return false;
    }
    // 空白をNumberにキャストすると0にされてしまう事への対応
    if (value.indexOf(' ') > -1 || value.indexOf('　') > -1) {
      return true;
    }
    // 先頭もしくは最後が "." の場合( ".1"、"1." など )は数値扱いしない
    if (value.charAt(0) === '.' || value.charAt(value.length - 1) === '.') {
      return true;
    }
    // 整数部分が0以外で先頭に0がついている場合( "000"、"01"、"012.3" など )は数値扱いしない
    if (value.split('.')[0].length > 1 && value.charAt(0) === '0') {
      return true;
    }
    return isNaN(Number(value));
  }

  /**
   * 指定された数値桁数(整数部、小数部)におさまっているかどうかを判定する
   * @param value		判定する文字列
   * @param figure	整数部の桁数
   * @param scale		小数部の桁数
   */
  static isValidNumberString(value: string, figure: number, scale: number): boolean {
    const number = Number(value);
    const max: number = Math.pow(10, Number(figure));
    const min: number = max * (-1);
    /* 数値かどうかのチェック */
    if (this.isNaNString(value)) {
      return false;     // 数値でない場合は無条件で false
    }
    /* 整数部のチェック */
    if (number >= max || number <= min) {
      return false;     // 指定の桁数より長い場合は false
    }
    /* 小数部のチェック */
    const numberArray: string[] = value.split('.');
    if (numberArray.length > 2) {
      return false;     // 小数点が複数ある場合は無条件で false
    }
    if (numberArray.length === 2) {	// 小数部がある場合
      if (numberArray[1].length > scale) {
        return false;   // 指定の桁数より長い場合は false
      }
    }
    return true;
  }

  /**
   * 整数かどうかを判定する
   * [戻り値]true：整数(0,正の整数,負の整数)／false：それ以外
   *
   * @param value 判定する文字列
   */
  static isIntegerString(value: string): boolean {
    if (this.isNaNString(value)) {
      return false;                 // 数値でない場合は無条件で false
    } else {
      if (value.indexOf('.') < 0) {
        return true;                // 小数点が無い場合
      } else {
        return false;               // 小数点がある場合
      }
    }
  }

  /**
   * 指定された数値以下(指定の数値と同じ場合も含む)かどうかを判定する
   * [戻り値]true：正の整数(指定以下の数／false：それ以外)
   *
   * @param value 判定する文字列
   * @param compare 比較する文字列
   */
  static isLessThanNumberString(value: string, compare: number): boolean {
    if (this.isNaNString(value)) {
      return false;		// 数値でない場合は無条件で false
    }
    if (Number(value) <= compare) {
      return true;		// 指定の数以下の場合
    } else {
      return false;		// 指定の数より大きい数の場合
    }
  }

  /**
   * 四捨五入処理
   *
   * @param value 四捨五入する数
   * @param digit 有効な小数桁
   * @param roundNum 切り上げる数(四捨五入の場合は"5")
   */
  static round(value: string, digit: number, roundNum = 5) {
    let valueArray: string[];     // 整数部と小数部をそれぞれ格納する配列
    let decimalArray: string[];   // 小数部を1文字ずつ格納する配列
    let returnValue: string;      // 四捨五入済みの値を格納するString
    let i: number;                // ループ用変数
    let addNum: number;           // 繰上げ計算用変数(整数で四捨五入する場合のみ使用)

    /* 入力値チェック1 */
    if (value == null || value === '') {
      return value;   // 値がない場合はそのまま返す
    }
    /* 入力値チェック2 */
    if (this.isNaNString(value)) {
      return value;   // 数値でない場合はそのまま返す
    }

    /* 整数部と小数部に分割 */
    valueArray = value.split('.');

    if (digit === 0) {        // ■整数に四捨五入する場合■
      if (this.isIntegerString(value)) {
        return value; // 小数でない場合はそのまま返す
      }
      /* 四捨五入処理(小数点以下第一位の数値で四捨五入) */
      if (Number(String(valueArray[1]).charAt(0)) >= roundNum) {
        returnValue = String(Number(valueArray[0]) + 1);  // 繰上がる場合
      } else {
        returnValue = valueArray[0];                      // 繰上がらない場合
      }
    } else if (digit < 0) {   // ■整数で四捨五入する場合■
      /* 整数部を1文字ずつ分割する */
      decimalArray = String(valueArray[0]).split('');
      /* 桁数チェック */
      if (decimalArray.length <= (digit * -1)) {
        return '0';	  // 桁数を超えた四捨五入をしようとした場合は0を返す
      }
      /* 繰上りチェック */
      if (Number(decimalArray[decimalArray.length + digit]) >= roundNum) {	// 繰上がる場合
        addNum = Math.pow(10, (digit * -1));
      } else {	// 繰上がらない場合
        addNum = 0;
      }
      /* 四捨五入して0になる部分に0をセット */
      for (i = decimalArray.length + digit; i < decimalArray.length; i++) {
        decimalArray[i] = '0';
      }
      /* 分割した整数部を連結し、繰上りを足す */
      returnValue = String(Number(decimalArray.join('')) + addNum);
    } else {                // ■小数で四捨五入する場合■
      if (this.isIntegerString(value)) {
        return value;		// 小数でない場合はそのまま返す
      }
      /* 小数部を1文字ずつ分割する */
      decimalArray = String(valueArray[1]).split('');
      /* 入力値チェック2 */
      if (decimalArray.length <= digit) {
        return value;		// 小数部が指定の桁数以下の場合はそのまま返す
      }
      /* 四捨五入処理 */
      for (i = digit; i >= 0; i--) {
        const checkNum = Number(decimalArray[i]);
        if (i === digit) {            // 最初の数の場合：四捨五入
          if (checkNum >= roundNum) {
            decimalArray[i - 1] = String(Number(decimalArray[i - 1]) + 1);
          }
          decimalArray[i] = '0';
        } else if (i === 0) {         // 最後の数(第1位)の場合：繰上りチェック(繰上りは整数部)
          if (checkNum >= 10) {
            valueArray[0] = String(Number(valueArray[0]) + 1);
            decimalArray[i] = '0';
          }
        } else if (checkNum >= 10) {  // 間の数の場合：繰上りチェック
          decimalArray[i - 1] = String(Number(decimalArray[i - 1]) + 1);
          decimalArray[i] = '0';
        } else {
          break;
        }
      }
      /* 整数部と小数部の連結 */
      returnValue = String(Number(valueArray[0])) + '.';
      for (i = 0; i < digit; i++) {
        returnValue += String(decimalArray[i]);
      }
    }

    return returnValue;
  }

  /**
   * 少数以下の桁数を取得する
   * @param 対象の値
   * @return 少数以下の桁数(例：0.12 ⇒ 2が返る)
   */
  private static getDecimalPoint(value: string): number {
    if (PlessViewUtilsSub.isEmpty(value) || this.isNaNString(value)) {
      return null;
    }
    const array = value.split('.');
    if (array.length > 1) {
      return array[1].toString().length;
    } else {
      return 0;
    }
  }

  /**
   * 丸め誤差の無い掛け算を行う
   * @param value1 値1
   * @param value2 値2
   * @return 値1×値2
   */
  static multiply(value1: string, value2: string): number {
    if (PlessViewUtilsSub.isEmpty(value1) || this.isNaNString(value1)) {
      value1 = '0';
    }
    if (PlessViewUtilsSub.isEmpty(value2) || this.isNaNString(value2)) {
      value2 = '0';
    }
    const intValue1 = Number(value1.replace('.', ''));
    const intValue2 = Number(value2.replace('.', ''));
    const decimalPoint = this.getDecimalPoint(value1) + this.getDecimalPoint(value2);
    return ((intValue1 * intValue2) / Math.pow(10, decimalPoint));
  }

  /**
   * 丸め誤差の無い足し算を行う
   * @param value1 値1
   * @param value2 値2
   * @return 値1＋値2
   */
  static add(value1: string, value2: string): number {
    if (PlessViewUtilsSub.isEmpty(value1) || this.isNaNString(value1)) {
      value1 = '0';
    }
    if (PlessViewUtilsSub.isEmpty(value2) || this.isNaNString(value2)) {
      value2 = '0';
    }
    const decimalPoint = Math.max(this.getDecimalPoint(value1), this.getDecimalPoint(value2));
    const num = Math.pow(10, decimalPoint);
    return ((this.multiply(value1, num.toString()) + this.multiply(value2, num.toString())) / num);
  }

  /**
   * 丸め誤差の無い引き算を行う
   * @param value1 値1
   * @param value2 値2
   * @return 値1－値2
   */
  static subtract(value1: string, value2: string): number {
    if (PlessViewUtilsSub.isEmpty(value1) || this.isNaNString(value1)) {
      value1 = '0';
    }
    if (PlessViewUtilsSub.isEmpty(value2) || this.isNaNString(value2)) {
      value2 = '0';
    }
    const decimalPoint = Math.max(this.getDecimalPoint(value1), this.getDecimalPoint(value2));
    const num = Math.pow(10, decimalPoint);
    return ((this.multiply(value1, num.toString()) - this.multiply(value2, num.toString())) / num);
  }

  /**
   * 掲載料請求枠数取得
   */
  static getPublishAmountLimitNum(section: SectionComponent, koma: KomaComponent): number {
    const ow: number = this.stiction((koma.sizeW / section.sizeW), section.divX);
    const oh: number = this.stiction((koma.sizeH / section.sizeH), section.divY);
    return ow * oh;
  }

  /**
   * 1をdiv分割した場合にvalueがどの範囲に位置するかをstep単位で取得する
   *
   * @param value (0～1)
   * @param div 分割数
   * @param step サイズ幅
   * @return 吸着した際の数値（０～divまでの範囲）
   */
  static stiction(value: number, div: number, step = 0.25): number {
    let rv = Number(0.0);
    let i = Number(0);

    for (i = step; i <= div; i += step) {
      if (value < Number(i / div)) {
        // さらに半数をしらべることでどちら側に吸着するか調べる
        rv = (value < Number((i - (step / 2.0)) / div)) ? i - step : i;
        break;
      }
    }
    if (i > div) {
      rv = div;
    }

    return rv;
  }

}
